import { useTranslation } from "react-i18next";

import { OnOffBadge } from "@/components/setup/on-off-badge";
import { DIETARY_TAGS, getMenuIcon } from "@/lib/menu";
import type { MenuConfig } from "@/lib/types";

const MAX_VISIBLE_GROUPS = 5;

export function MenuPreviewCard({ menu, on }: { menu: MenuConfig | undefined; on: boolean }) {
  const { t } = useTranslation();
  const groups = menu?.groups ?? [];
  const itemCount = groups.reduce((n, g) => n + g.items.length, 0);
  const dietaryKeys = new Set(groups.flatMap((g) => g.items.flatMap((it) => it.dietary ?? [])));
  const dietaryLabels = DIETARY_TAGS.filter((d) => dietaryKeys.has(d.key));

  return (
    <div className="space-y-3 bg-card p-4">
      <div className="flex items-center justify-between">
        <p className="font-medium">{t("setup.preview.menuTitle")}</p>
        <OnOffBadge on={on} />
      </div>
      {groups.length > 0 ? (
        <>
          <div className="flex flex-wrap gap-1.5">
            {groups.slice(0, MAX_VISIBLE_GROUPS).map((g) => {
              const Icon = getMenuIcon(g.icon);
              return (
                <span
                  key={g.id}
                  className="inline-flex items-center gap-1 rounded-full border bg-secondary/40 px-2.5 py-1 text-xs"
                >
                  <Icon className="h-3.5 w-3.5" /> {g.name || t("setup.preview.courseFallback")}
                </span>
              );
            })}
            {groups.length > MAX_VISIBLE_GROUPS && (
              <span className="rounded-full border px-2.5 py-1 text-xs text-muted-foreground">
                {t("setup.preview.more", { count: groups.length - MAX_VISIBLE_GROUPS })}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {t("setup.preview.courses", { count: groups.length })},{" "}
            {t("menuEditor.items", { count: itemCount })}
            {dietaryLabels.length > 0 &&
              `, ${t("setup.preview.dietaryOptions", {
                tags: dietaryLabels.map((d) => t(d.labelKey)).join(", "),
              })}`}
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{t("setup.preview.noDishes")}</p>
      )}
    </div>
  );
}
